import { addDays, addMinutes, format, isAfter, isValid, parse, parseISO } from 'date-fns';

type DateFormatType = 'time' | 'date' | 'full' | 'short' | 'custom';

/**
 * Formats an ISO date string into various formats using date-fns.
 *
 * @param {string} dateStr - The ISO date string to format.
 * @param {DateFormatType} [formatType] - The type of format to apply ('time', 'date', 'full', 'short', 'custom').
 * @param {string} [customFormat] - Custom format string (used if formatType is 'custom').
 * @param {string} [timezone] - Optional IANA timezone name (e.g., 'America/New_York').
 * @returns {string | undefined} The formatted date string, or undefined if parsing fails.
 *
 * @example
 * formatIsoDate('2025-09-23T14:30:00Z', 'full'); // "September 23, 2025 2:30 PM"
 * @example
 * formatIsoDate('2025-09-23T14:30:00Z', 'custom', 'yyyy/MM/dd HH:mm'); // "2025/09/23 14:30"
 */
const formatIsoDate = (
  dateStr: string,
  formatType?: DateFormatType,
  customFormat?: string,
  timezone?: string,
): string | undefined => {
  if (!dateStr) return 'N/A';
  const parsed = parseISO(dateStr);
  if (timezone) {
    const zonedDate = new Date(parsed.toLocaleString('en-US', { timeZone: timezone }));
    return format(zonedDate, 'MMMM d, yyyy h:mm a');
  }
  switch (formatType) {
    case 'time':
      return format(parsed, 'h:mm a');
    case 'date':
      return format(parsed, 'MMMM d, yyyy');
    case 'full':
      return format(parsed, 'MMMM d, yyyy h:mm a');
    case 'short':
      return format(parsed, 'MM/dd/yyyy');
    case 'custom':
      return customFormat ? format(parsed, customFormat) : dateStr;
    default:
      return dateStr;
  }
};

/**
 * Formats a non-ISO date string using provided or default formats.
 *
 * @param {string} [dateStr] - The non-ISO date string to format.
 * @param {string} [formatIn='MM-dd-yyyy'] - Input format for parsing the date string.
 * @returns {string | undefined} The formatted date string, or undefined if parsing fails.
 *
 * @example
 * formatNonIsoDate('09-23-2025'); // "09-23-2025"
 * @example
 * formatNonIsoDate('09/23/2025', 'MM/dd/yyyy'); // "09/23/2025"
 */
const formatNonIsoDate = (dateStr?: string, formatIn = 'MM-dd-yyyy') => {
  if (!dateStr) {
    return format(new Date(), formatIn);
  }
  const formats = ['MM/dd/yyyy', 'MM-dd-yyyy', 'M/d/yyyy h:mm:ss a'];
  let parsedDate: Date | undefined = undefined;
  for (const fmt of formats) {
    const tryParsing = parse(dateStr, fmt, new Date());
    if (isValid(tryParsing) && !isNaN(tryParsing?.getTime())) {
      parsedDate = tryParsing;
      break;
    }
  }
  return parsedDate && isValid(parsedDate) && !isNaN(parsedDate.getTime()) ? format(parsedDate, formatIn) : undefined;
};

/**
 * Formats a date string into various formats using date-fns.
 *
 * @param {string} dateStr - The date string to format (ISO or custom).
 * @param {string} [formatIn] - Input format for parsing non-ISO date strings (e.g., 'MM-dd-yyyy').
 * @param {DateFormatType} [formatType] - The type of format to apply ('time', 'date', 'full', 'short', 'custom').
 * @param {string} [customFormat] - Custom format string (used if formatType is 'custom').
 * @param {string} [timezone] - Optional IANA timezone name (e.g., 'America/New_York').
 * @returns {string | undefined} The formatted date string, or undefined if parsing fails.
 *
 * @example
 * // Format ISO string as full date/time
 * formatDate('2025-09-23T14:30:00Z', undefined, 'full'); // "September 23, 2025 2:30 PM"
 *
 * @example
 * // Format ISO string as time only
 * formatDate('2025-09-23T14:30:00Z', undefined, 'time'); // "2:30 PM"
 *
 * @example
 * // Format with custom format
 * formatDate('2025-09-23T14:30:00Z', undefined, 'custom', 'yyyy/MM/dd HH:mm'); // "2025/09/23 14:30"
 *
 * @example
 * // Format with timezone
 * formatDate('2025-09-23T14:30:00Z', undefined, 'full', undefined, 'America/New_York'); // "September 23, 2025 10:30 AM"
 *
 * @example
 * // Format non-ISO string with input format
 * formatDate('09-23-2025', 'MM-dd-yyyy'); // "09-23-2025"
 */
export const formatDate = (
  dateStr: string,
  formatIn?: string,
  formatType?: DateFormatType,
  customFormat?: string,
  timezone?: string,
): string | undefined => {
  if (formatType) {
    return formatIsoDate(dateStr, formatType, customFormat, timezone);
  }
  return formatNonIsoDate(dateStr, formatIn);
};

export const getVerificationExpiryTime = (isoString: string, minutesToAdd = 30, includeSeconds = false): string => {
  if (!isoString) return 'N/A';
  const date = parseISO(isoString);
  const expiresAt = addMinutes(date, minutesToAdd);
  return format(expiresAt, includeSeconds ? 'h:mm:ss a' : 'h:mm a');
};

export const getVerificationExpiryDate = (isoString: string, daysToAdd = 90): string => {
  if (!isoString) return 'N/A';
  const date = parseISO(isoString);
  const expiryDate = addDays(date, daysToAdd);
  return format(expiryDate, 'PPPp'); // June 5, 2025 at 2:31 PM
};

export const isPastExpiry = (isoString: string, daysToAdd = 90): boolean => {
  if (!isoString) return false;
  const expiryDate = addDays(parseISO(isoString), daysToAdd);
  return isAfter(new Date(), expiryDate);
};

/**
 * Converts a date string to ISO date format (yyyy-MM-dd).
 * Handles multiple input formats: MM/dd/yyyy, ISO datetime, yyyy-MM-dd.
 *
 * @param {string | undefined} dateStr - The date string to convert.
 * @returns {string | undefined} The date in yyyy-MM-dd format, or undefined if empty/invalid.
 *
 * @example
 * toIsoDateString('09/08/2011'); // "2011-09-08"
 * toIsoDateString('2011-09-08T14:51:22'); // "2011-09-08"
 * toIsoDateString('2011-09-08'); // "2011-09-08"
 */
export const toIsoDateString = (dateStr: string | undefined): string | undefined => {
  if (!dateStr || dateStr.trim() === '') {
    return undefined;
  }

  // Already in yyyy-MM-dd format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // ISO datetime format - extract date part
  if (/^\d{4}-\d{2}-\d{2}T/.test(dateStr)) {
    return dateStr.split('T')[0];
  }

  // Try parsing common formats
  const formats = ['MM/dd/yyyy', 'M/d/yyyy', 'MM-dd-yyyy'];
  for (const fmt of formats) {
    const parsed = parse(dateStr, fmt, new Date());
    if (isValid(parsed)) {
      return format(parsed, 'yyyy-MM-dd');
    }
  }

  return undefined;
};

/**
 * Converts a date string to display format (MM/dd/yyyy).
 * Handles multiple input formats: MM/dd/yyyy, ISO datetime, yyyy-MM-dd.
 *
 * @param {string | undefined} dateStr - The date string to convert.
 * @returns {string} The date in MM/dd/yyyy format, or empty string if empty/invalid.
 *
 * @example
 * toDisplayDateString('2011-09-08'); // "09/08/2011"
 * toDisplayDateString('2011-09-08T14:51:22'); // "09/08/2011"
 * toDisplayDateString('09/08/2011'); // "09/08/2011"
 */
export const toDisplayDateString = (dateStr: string | undefined): string => {
  if (!dateStr || dateStr.trim() === '') {
    return '';
  }

  // Already in MM/dd/yyyy format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  // ISO datetime format - parse and format
  if (/^\d{4}-\d{2}-\d{2}T/.test(dateStr)) {
    const parsed = parseISO(dateStr);
    if (isValid(parsed)) {
      return format(parsed, 'MM/dd/yyyy');
    }
  }

  // yyyy-MM-dd format - parse and format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parsed = parseISO(dateStr);
    if (isValid(parsed)) {
      return format(parsed, 'MM/dd/yyyy');
    }
  }

  return dateStr;
};
